import { FC } from 'react'
import { CondicaoCatalogo } from '../../formularios/core/condicao/catalogo'
import {
  CampoDicionario as CampoDicionarioTradutor,
  DicionarioCondicao,
  descreverCondicao,
} from '../../formularios/core/condicao/descreverCondicao'
import {
  CampoDicionario,
  CampoFormulario,
  ComparacaoProtocolo as Comparacao,
  DiferencaCampo,
  DiferencaRegra,
  RegraProtocolo,
  TipoDiferenca,
} from '../core/_models'
import { TriagemRiscoBadge } from '../../../../components/TriagemRisco/TriagemRiscoBadge'

type Props = {
  comparacao?: Comparacao
  catalogo?: CondicaoCatalogo
  titulo?: string
}

const ROTULO_TIPO: Record<TipoDiferenca, string> = {
  ADICIONADO: 'Adicionada',
  REMOVIDO: 'Removida',
  ALTERADO: 'Alterada',
}

const ROTULO_TIPO_CAMPO: Record<TipoDiferenca, string> = {
  ADICIONADO: 'Adicionado',
  REMOVIDO: 'Removido',
  ALTERADO: 'Alterado',
}

const ICONE_TIPO: Record<TipoDiferenca, string> = {
  ADICIONADO: '+',
  REMOVIDO: '−',
  ALTERADO: '±',
}

const ROTULO_ATRIBUTO: Record<string, string> = {
  CONDICAO: 'condição',
  COR: 'cor',
  GRUPO: 'grupo',
  JUSTIFICATIVA: 'justificativa',
  FONTE: 'fonte',
  ORDEM: 'ordem',
  ROTULO: 'rótulo',
  TIPO: 'tipo',
  OBRIGATORIO: 'obrigatório',
  MULTIPLO: 'múltiplo',
  OPCOES: 'opções',
  CONDICAO_EXIBICAO: 'condição de exibição',
}

const dicionarioDoLado = (
  campos: Record<string, CampoDicionario> | undefined,
  catalogo?: CondicaoCatalogo
): DicionarioCondicao => ({
  campos: Object.entries(campos ?? {}).reduce<Record<string, CampoDicionarioTradutor>>(
    (acumulado, [codigo, campo]) => {
      acumulado[codigo] = { rotulo: campo.rotulo, tipo: campo.tipo, opcoes: campo.opcoes }
      return acumulado
    },
    {}
  ),
  catalogo,
})

const Marcacao: FC<{ tipo: TipoDiferenca; atributos: string[]; campo?: boolean }> = ({
  tipo,
  atributos,
  campo,
}) => {
  const base = campo ? ROTULO_TIPO_CAMPO[tipo] : ROTULO_TIPO[tipo]
  const detalhe =
    tipo === 'ALTERADO' && atributos.length > 0
      ? `: ${atributos.map((atributo) => ROTULO_ATRIBUTO[atributo] ?? atributo).join(', ')}`
      : ''
  return (
    <span>
      <span aria-hidden='true'>{ICONE_TIPO[tipo]} </span>
      {base}
      {detalhe}
    </span>
  )
}

const LadoRegra: FC<{ regra?: RegraProtocolo | null; dicionario: DicionarioCondicao }> = ({
  regra,
  dicionario,
}) => {
  if (!regra) {
    return <span className='text-muted'>—</span>
  }
  return (
    <div>
      <div>{descreverCondicao(regra.condicao, dicionario).texto}</div>
      <div className='mt-2'>
        <TriagemRiscoBadge cor={regra.cor} />
      </div>
      <small className='text-muted d-block mt-1'>Grupo: {regra.grupo}</small>
    </div>
  )
}

const LadoCampo: FC<{ campo?: CampoFormulario | null; dicionario: DicionarioCondicao }> = ({
  campo,
  dicionario,
}) => {
  if (!campo) {
    return <span className='text-muted'>—</span>
  }
  return (
    <div>
      <div>{campo.rotulo}</div>
      <small className='text-muted d-block'>
        {campo.tipo}
        {campo.obrigatorio ? ' · obrigatório' : ''}
      </small>
      {campo.condicaoExibicao && (
        <small className='d-block'>
          Exibe quando: {descreverCondicao(campo.condicaoExibicao, dicionario).texto}
        </small>
      )}
    </div>
  )
}

/**
 * Diff lado a lado entre duas versões (T7a AC3). A diferença é comunicada por <b>texto</b>
 * ("Adicionada", "Alterada: condição, cor") com ícone — nunca só por cor.
 */
export const ComparacaoProtocolo: FC<Props> = ({ comparacao, catalogo, titulo }) => {
  if (!comparacao) {
    return null
  }
  const antes = dicionarioDoLado(comparacao.dicionario?.antes, catalogo)
  const depois = dicionarioDoLado(comparacao.dicionario?.depois, catalogo)
  const semDiferenca =
    (comparacao.regras ?? []).length === 0 && (comparacao.campos ?? []).length === 0

  const rotuloAntes = comparacao.antes ? `Versão ${comparacao.antes.versao}` : 'Sem versão anterior'
  const rotuloDepois = `Versão ${comparacao.depois?.versao ?? ''}`

  return (
    <section aria-label={titulo ?? 'Comparação entre versões'}>
      {titulo && <h4 className='fw-bold mb-4'>{titulo}</h4>}
      {semDiferenca ? (
        <p className='text-muted'>Nenhuma diferença entre as versões</p>
      ) : (
        <>
          <div className='table-responsive mb-8'>
            <table className='table table-row-bordered align-middle'>
              <caption>
                Regras — diferenças entre {rotuloAntes} e {rotuloDepois}
              </caption>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th scope='col'>Código</th>
                  <th scope='col'>Diferença</th>
                  <th scope='col'>Antes ({rotuloAntes})</th>
                  <th scope='col'>Depois ({rotuloDepois})</th>
                </tr>
              </thead>
              <tbody>
                {(comparacao.regras ?? []).map((diferenca: DiferencaRegra) => (
                  <tr key={diferenca.codigo}>
                    <td>
                      <code>{diferenca.codigo}</code>
                    </td>
                    <td>
                      <Marcacao tipo={diferenca.tipo} atributos={diferenca.atributos ?? []} />
                    </td>
                    <td>
                      <LadoRegra regra={diferenca.antes} dicionario={antes} />
                    </td>
                    <td>
                      <LadoRegra regra={diferenca.depois} dicionario={depois} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(comparacao.campos ?? []).length > 0 && (
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle'>
                <caption>
                  Campos do formulário — diferenças entre {rotuloAntes} e {rotuloDepois}
                </caption>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th scope='col'>Código</th>
                    <th scope='col'>Diferença</th>
                    <th scope='col'>Antes ({rotuloAntes})</th>
                    <th scope='col'>Depois ({rotuloDepois})</th>
                  </tr>
                </thead>
                <tbody>
                  {(comparacao.campos ?? []).map((diferenca: DiferencaCampo) => (
                    <tr key={diferenca.codigo}>
                      <td>
                        <code>{diferenca.codigo}</code>
                      </td>
                      <td>
                        <Marcacao
                          tipo={diferenca.tipo}
                          atributos={diferenca.atributos ?? []}
                          campo
                        />
                      </td>
                      <td>
                        <LadoCampo campo={diferenca.antes} dicionario={antes} />
                      </td>
                      <td>
                        <LadoCampo campo={diferenca.depois} dicionario={depois} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
